import React, { useEffect, useState } from "react";
import axios from "axios";
import API_BASE_URL from "../../../services/api";
import {
  PlusCircle,
  Edit2,
  Trash2,
  Search,
  X,
  Check,
  Loader2,
  Building2,
  MapPin,
  Phone,
  Navigation,
  ChevronRight,
  Filter,
  MoreVertical
} from "lucide-react";

const API = `${API_BASE_URL}/api/diagnostic-centers`;
const CITY_API = `${API_BASE_URL}/api/cities`;

const DiagnosticCenter = () => {
  const [list, setList] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const [form, setForm] = useState({
    name: "",
    address: "",
    pincode: "",
    cityId: ""
  });

  const [editId, setEditId] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [centersRes, citiesRes] = await Promise.all([
        axios.get(API),
        axios.get(CITY_API)
      ]);
      setList(centersRes.data.data);
      setCities(citiesRes.data.data);
    } catch (err) {
      setError("Failed to load data. Please try again.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.name.trim() || !form.cityId) {
      setError("Center name and city are required");
      return;
    }

    setSubmitLoading(true);
    setError(null);
    setSuccess(null);

    try {
      if (editId) {
        await axios.put(`${API}/${editId}`, form);
        setSuccess("Diagnostic center updated successfully!");
      } else {
        await axios.post(API, form);
        setSuccess("Diagnostic center added successfully!");
      }

      setForm({ name: "", address: "", pincode: "", cityId: "" });
      setEditId(null);
      fetchData();
      
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.response?.data?.message || "An error occurred. Please try again.");
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleEdit = (item) => {
    setForm({
      name: item.name || "",
      address: item.address || "",
      pincode: item.pincode || "",
      cityId: item.cityId || ""
    });
    setEditId(item.id);
    setError(null);
    
    // Scroll to form
    const formElement = document.getElementById("center-form");
    if (formElement) {
      formElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this diagnostic center?")) return;
    
    try {
      await axios.delete(`${API}/${id}`);
      setSuccess("Diagnostic center deleted successfully!");
      fetchData();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError("Failed to delete diagnostic center. Please try again.");
    }
  };

  const handleCancel = () => {
    setForm({ name: "", address: "", pincode: "", cityId: "" });
    setEditId(null);
    setError(null);
  };

  const filteredCenters = list.filter(center => {
    const searchLower = searchTerm.toLowerCase();
    return (
      center.name?.toLowerCase().includes(searchLower) ||
      center.city?.name?.toLowerCase().includes(searchLower) ||
      center.pincode?.toString().includes(searchTerm) ||
      center.address?.toLowerCase().includes(searchLower)
    );
  });

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <div className="min-h-screen  ">
      <div >
       

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Left Column - Form */}
          <div className="lg:col-span-1" id="center-form">
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-5">
              <div className="flex items-center gap-2 mb-5">
                <div className="p-1.5 bg-blue-50 rounded-md">
                  <Building2 className="w-4 h-4 text-blue-600" />
                </div>
                <h2 className="text-base font-semibold text-gray-800">
                  {editId ? "Edit Center" : "Add Center"}
                </h2>
              </div>
              
              {/* Messages */}
              {success && (
                <div className="mb-4 p-2.5 bg-green-50 border border-green-100 rounded-md flex items-center gap-2 text-xs">
                  <Check className="w-3.5 h-3.5 text-green-600" />
                  <span className="text-green-700">{success}</span>
                </div>
              )}

              {error && (
                <div className="mb-4 p-2.5 bg-red-50 border border-red-100 rounded-md flex items-center gap-2 text-xs">
                  <X className="w-3.5 h-3.5 text-red-600" />
                  <span className="text-red-700">{error}</span>
                </div>
              )}

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">
                    Center Name *
                  </label>
                  <input
                    type="text"
                    placeholder="Enter center name"
                    value={form.name}
                    onChange={(e) => {
                      setForm({ ...form, name: e.target.value });
                      setError(null);
                    }}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 outline-none"
                    disabled={submitLoading}
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">
                    City *
                  </label>
                  <select
                    value={form.cityId}
                    onChange={(e) => {
                      setForm({ ...form, cityId: e.target.value });
                      setError(null);
                    }}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 outline-none"
                    disabled={submitLoading}
                  >
                    <option value="">Select a city</option>
                    {cities.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">
                    Address
                  </label>
                  <textarea
                    placeholder="Enter complete address"
                    value={form.address}
                    onChange={(e) => {
                      setForm({ ...form, address: e.target.value });
                      setError(null);
                    }}
                    rows="2"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 outline-none resize-none"
                    disabled={submitLoading}
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">
                    Pincode
                  </label>
                  <input
                    type="text"
                    placeholder="Enter pincode"
                    value={form.pincode}
                    onChange={(e) => {
                      setForm({ ...form, pincode: e.target.value });
                      setError(null);
                    }}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 outline-none"
                    disabled={submitLoading}
                  />
                </div>

                <div className="flex gap-2 pt-1">
                  <button
                    type="submit"
                    disabled={submitLoading || !form.name.trim() || !form.cityId}
                    className="flex-1 bg-blue-600 text-white px-4 py-2 text-sm font-medium rounded-md hover:bg-blue-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1.5"
                  >
                    {submitLoading ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : editId ? (
                      <>
                        <Edit2 className="w-3.5 h-3.5" />
                        Update Center
                      </>
                    ) : (
                      <>
                        <PlusCircle className="w-3.5 h-3.5" />
                        Add Center
                      </>
                    )}
                  </button>
                  
                  {editId && (
                    <button
                      type="button"
                      onClick={handleCancel}
                      className="px-3 py-2 text-sm border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors duration-200"
                      disabled={submitLoading}
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </form>

              {/* Stats */}
              <div className="mt-6 pt-5 border-t border-gray-100">
                <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-3">
                  Overview
                </h3>
                <div className="grid grid-cols-2 gap-2">
                  <div className="bg-gray-50 p-3 rounded-md border border-gray-100">
                    <div className="text-lg font-semibold text-gray-800">{list.length}</div>
                    <div className="text-xs text-gray-500 mt-0.5">Total Centers</div>
                  </div>
                  <div className="bg-blue-50 p-3 rounded-md border border-blue-100">
                    <div className="text-lg font-semibold text-blue-700">{cities.length}</div>
                    <div className="text-xs text-blue-600 mt-0.5">Cities</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Centers List */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
              {/* Table Header */}
              <div className="p-5 border-b border-gray-200">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                  <div>
                    <h2 className="text-base font-semibold text-gray-800">All Diagnostic Centers</h2>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {filteredCenters.length} of {list.length} centers
                    </p>
                  </div>
                  
                  <div className="relative w-full sm:w-56">
                    <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search centers..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 outline-none"
                    />
                    {searchTerm && (
                      <button
                        onClick={() => setSearchTerm("")}
                        className="absolute right-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Table Content */}
              <div className="overflow-hidden">
                {loading ? (
                  <div className="flex flex-col items-center justify-center py-12">
                    <Loader2 className="w-6 h-6 text-blue-600 animate-spin mb-2" />
                    <p className="text-sm text-gray-600">Loading diagnostic centers...</p>
                  </div>
                ) : filteredCenters.length === 0 ? (
                  <div className="text-center py-10">
                    <div className="mx-auto w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                      <Building2 className="w-5 h-5 text-gray-400" />
                    </div>
                    <h3 className="text-sm font-medium text-gray-700 mb-1">
                      {searchTerm ? "No centers found" : "No diagnostic centers yet"}
                    </h3>
                    <p className="text-xs text-gray-500 max-w-xs mx-auto">
                      {searchTerm
                        ? "Try adjusting your search terms"
                        : "Get started by adding your first diagnostic center"}
                    </p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="py-3 px-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-b border-gray-200">
                            Center Details
                          </th>
                          <th className="py-3 px-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-b border-gray-200">
                            Location
                          </th>
                          <th className="py-3 px-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-b border-gray-200 w-28">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {filteredCenters.map((center) => (
                          <tr 
                            key={center.id} 
                            className="hover:bg-gray-50 transition-colors duration-150"
                          >
                            <td className="py-3 px-4">
                              <div className="flex items-start gap-3">
                                <div className="flex-shrink-0 w-8 h-8 bg-blue-50 rounded flex items-center justify-center mt-0.5">
                                  <Building2 className="w-4 h-4 text-blue-600" />
                                </div>
                                <div>
                                  <h4 className="text-sm font-medium text-gray-800">{center.name}</h4>
                                  {center.address && (
                                    <div className="flex items-start gap-1.5 mt-1.5">
                                      <Navigation className="w-3.5 h-3.5 text-gray-400 flex-shrink-0 mt-0.5" />
                                      <p className="text-xs text-gray-600">{center.address}</p>
                                    </div>
                                  )}
                                  {center.pincode && (
                                    <div className="flex items-center gap-1.5 mt-1">
                                      <MapPin className="w-3.5 h-3.5 text-gray-400" />
                                      <span className="text-xs text-gray-500">Pincode: {center.pincode}</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="py-3 px-4">
                              <div className="flex items-center gap-2">
                                <div className="w-6 h-6 bg-gray-100 rounded flex items-center justify-center">
                                  <span className="text-xs font-medium text-gray-600">
                                    {center.city?.name?.charAt(0).toUpperCase()}
                                  </span>
                                </div>
                                <div>
                                  <span className="text-sm text-gray-800 font-medium">
                                    {center.city?.name}
                                  </span>
                                  <div className="text-xs text-gray-500 mt-0.5">
                                    City ID: {center.cityId}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="py-3 px-4">
                              <div className="flex items-center gap-1">
                                <button
                                  onClick={() => handleEdit(center)}
                                  className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-md transition-colors duration-200"
                                  title="Edit center"
                                >
                                  <Edit2 className="w-3.5 h-3.5" />
                                </button>
                                <button
                                  onClick={() => handleDelete(center.id)}
                                  className="p-1.5 text-red-600 hover:bg-red-50 rounded-md transition-colors duration-200"
                                  title="Delete center"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Table Footer */}
              {filteredCenters.length > 0 && (
                <div className="px-5 py-3 border-t border-gray-200">
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <div>
                      Showing <span className="font-medium">{filteredCenters.length}</span> centers
                      {searchTerm && (
                        <span className="ml-2">
                          for "<span className="font-medium">{searchTerm}</span>"
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-1">
                        <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                        <span>Active</span>
                      </div>
                      <div className="text-gray-400">|</div>
                      <div>Updated: {new Date().toLocaleDateString('en-US', { 
                        month: 'short', 
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}</div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Help Text */}
            <div className="mt-4 text-xs text-gray-500">
              <p>• Required fields are marked with *</p>
              <p className="mt-0.5">• Centers are automatically available for appointment scheduling</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DiagnosticCenter;